import React, { Suspense, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { AnimatedResident } from '../components/AnimatedResident';
import SafeMount from '../components/SafeMount';
import { player, TP_TEST_BODY } from './player';
import { WALK_SPEED } from './walkBody';
import { useDriveStore } from './drive/driveStore';
import { camBasis } from '../services/worldObjects';
import { heliBridge } from '../city/heliTour';
import { useBodyStore, mannequinRig, useMannequinList } from './mannequins';

// ── YOUR OWN BODY ────────────────────────────────────────────────────────────
//
// In third person the camera stands behind you, so there has to be a you,
// walking when you walk, idling when you stop, turned to face where you are
// going rather than where the camera points — so you can look around a corner
// without your body spinning to follow.
//
// ⚠ IT IS NEVER YOUR ARTIST'S BODY ANY MORE (Khari, 2026-10-23: *"turn it off.
// the artists will live their own lives"*). It used to be: a member walked the
// city inside their artist's rigged model. Now it is the MANNEQUIN you chose on
// the selection screen (game/mannequins, 2026-11-01: "bring back the 3rd person
// view … generic manneuquins"). `?tpbody=<id>` still borrows a roster body for
// testing the camera, and wins over the mannequin.

export default function PlayerBody() {
  const npcList = useWorldStore((s) => s.npcList);
  const driving = useDriveStore((s) => s.driving) != null;
  const want = TP_TEST_BODY;
  const chosen = useBodyStore((s) => s.body);
  const listV = useMannequinList((s) => s.v);   // the API's set arrived: re-resolve
  const rig = useMemo(
    () => (want ? (npcList || []).find((n) => String(n.avatarId) === String(want) && n.rigged) : null)
      || mannequinRig(chosen),
    [npcList, want, chosen, listV], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const group = useRef();
  const yaw = useRef(0);
  const [moving, setMoving] = useState(false);
  const [rate, setRate] = useState(1);

  useFrame((_, dt) => {
    const g = group.current;
    if (!g) return;
    const show = player.third && !driving && !heliBridge.aboard;   // aboard a heli tour: you are inside it
    // Appearing (third person switched on, or out of a car): face where the
    // camera is looking, not the model's default +z — which is straight at it.
    if (show && !g.visible) yaw.current = Math.atan2(camBasis.fx, camBasis.fz);
    g.visible = show;
    if (!show) return;
    g.position.set(player.x, (player.y || 0) + player.jump, player.z);   // y: a room's floor in third person
    player.height = rig?.height || 0.85;   // the chase camera frames THIS body
    const sp = Math.hypot(player.vx, player.vz);
    if (sp > 0.3 || player.faceYaw != null) {
      // Turn toward the direction of travel, the short way round — or, standing
      // still, toward where you are being turned (player.faceYaw: the elevator).
      const target = sp > 0.3 ? Math.atan2(player.vx, player.vz) : player.faceYaw;
      let d = target - yaw.current;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      yaw.current += d * Math.min(1, dt * (sp > 0.3 ? 12 : 7));
    }
    g.rotation.y = yaw.current;
    const m = sp > 0.5;
    if (m !== moving) setMoving(m);
    // The walk clip was authored at a stroll; speed it up with the body so the
    // feet keep up (stepped, so React only hears about real changes).
    const r = m ? Math.max(1, Math.round((sp / WALK_SPEED) * 1.6 * 4) / 4) : 1;
    if (r !== rate) setRate(r);
  });

  if (!rig) return null;
  return (
    <group ref={group} visible={false}>
      <SafeMount
        key={rig.rigged}
        label={`player body ${rig.avatarId ?? `mannequin ${rig.key}`}`}
        retryMs={30000}
        onRetry={() => useGLTF.clear([rig.rigged, rig.walk, rig.idle].filter(Boolean))}
      >
        <Suspense fallback={null}>
          <AnimatedResident
            meshUrl={rig.rigged}
            rigPose={rig.pose || null}
            clipMap={rig.clips || null}
            clipUrls={[rig.walk, rig.idle].filter(Boolean)}
            play={moving ? 'walk' : 'idle'}
            timeScale={rate}
            height={rig.height || 1.8}
            position={[0, 0.02, 0]}
          />
        </Suspense>
      </SafeMount>
    </group>
  );
}
